import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Users, Link as LinkIcon, CurrencyDollar,
  ChartBar, IdentificationCard, DiscordLogo, Eye, EyeSlash, Sparkle, ArrowsClockwise, Tag, PaperPlaneTilt,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import FunnelBars from "@/components/FunnelBars";
import MemberDrawer from "@/components/owner/MemberDrawer";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const TABS = [
  { key: "overview", label: "OVERVIEW", icon: ChartBar },
  { key: "members", label: "FANS", icon: Users },
  { key: "links", label: "LINKS", icon: LinkIcon },
  { key: "sales", label: "SALES", icon: CurrencyDollar },
  { key: "profile", label: "PROFILE", icon: IdentificationCard },
];

const STAGE_META = {
  paid: { label: "PAID", color: "text-emerald-400 border-emerald-500/40" },
  played: { label: "PLAYED", color: "text-neon-cyan border-cyan-500/40" },
  discord_only: { label: "DISCORD", color: "text-neon-yellow border-yellow-500/40" },
  invited: { label: "INVITED", color: "text-white/40 border-white/20" },
};

function playtimeDisplay(mm) {
  if (mm.is_playing_now && mm.fivem_opened_at) {
    const startMs = new Date(mm.fivem_opened_at).getTime();
    if (!Number.isNaN(startMs)) {
      const mins = Math.max(0, Math.round((Date.now() - startMs) / 60000));
      const h = Math.floor(mins / 60);
      return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
    }
  }
  if (mm.fivem_minutes && mm.fivem_minutes > 0) {
    const mins = Math.round(mm.fivem_minutes);
    const h = Math.floor(mins / 60);
    return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
  }
  return "—";
}

export default function CreatorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [openMember, setOpenMember] = useState(null);
  const [profile, setProfile] = useState({});
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [sendingCreds, setSendingCreds] = useState(false);
  const [minting, setMinting] = useState(false);
  const [oow, setOow] = useState(null);
  const [oowOpen, setOowOpen] = useState(false);

  async function openOow() {
    setOowOpen(true);
    setOow(null);
    try {
      const r = await api.get(`/creators/${id}/out-of-window`);
      setOow(r.data);
    } catch (e) { toast.error("Failed to load out-of-window purchases"); }
  }

  async function load() {
    try {
      const r = await api.get(`/creators/${id}/detail`);
      setData(r.data);
      setProfile({ ...r.data.creator });
    } catch (e) {
      toast.error("Not found");
      nav("/owner/creators");
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  function copy(t) { navigator.clipboard.writeText(t); toast.success("Copied"); }

  async function togglePublish(next) {
    try {
      await api.patch(`/creators/${id}`, { earnings_published: next });
      toast.success(next ? "Earnings published to creator portal" : "Earnings hidden from creator");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function togglePromoPublish(next) {
    try {
      await api.patch(`/creators/${id}`, { promo_published: next });
      toast.success(next ? "Promo published to creator portal" : "Promo hidden from creator");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function mintInvite() {
    setMinting(true);
    try {
      const r = await api.post("/discord/mint-invite", { creator_id: id });
      toast.success(`Unique invite created: ${r.data.invite_url}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate invite");
    } finally { setMinting(false); }
  }

  async function savePassword() {
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    setSavingPw(true);
    try {
      await api.post(`/creators/${id}/set-password`, { password: newPw });
      toast.success("Password set — share it with the creator");
      setNewPw("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingPw(false); }
  }

  async function sendCredentials() {
    if (!data?.creator?.email) {
      return toast.error("Add a LOGIN EMAIL and Save Profile first.");
    }
    const auto = !newPw;
    const label = auto
      ? `Email a new auto-generated password to ${data.creator.email}?`
      : `Email the password you typed to ${data.creator.email}?`;
    if (!window.confirm(label)) return;
    setSendingCreds(true);
    try {
      const r = await api.post(`/creators/${id}/send-credentials`, newPw ? { password: newPw } : {});
      toast.success(`Sent to ${r.data.sent_to} · new PW: ${r.data.password}`);
      setNewPw("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send credentials");
    } finally { setSendingCreds(false); }
  }

  async function saveProfile() {
    try {
      const changed = {};
      Object.keys(profile).forEach((k) => {
        if (["id", "slug", "created_at", "discord_username"].includes(k)) return;
        if (profile[k] !== data.creator[k]) changed[k] = profile[k];
      });
      ["commission_pct", "attribution_window_days", "payout_min_threshold"].forEach((k) => {
        if (changed[k] !== undefined && changed[k] !== "" && changed[k] !== null) changed[k] = Number(changed[k]);
      });
      if (Object.keys(changed).length === 0) return toast.info("No changes");
      await api.patch(`/creators/${id}`, changed);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  if (!data) return <div className="kbd-label text-neon-cyan">// LOADING</div>;
  const c = data.creator;
  const m = data.metrics;
  const shareUrl = c.discord_invite_url || null;

  return (
    <div className="space-y-6">
      <Link to="/owner/creators" className="kbd-label text-white/50 hover:text-neon-cyan inline-flex items-center gap-1">
        <ArrowLeft size={12} /> BACK TO CREATORS
      </Link>

      {/* Header */}
      <header className="card-tactical p-6">
        <div className="flex items-start gap-4">
          {c.avatar_url ? (
            <img src={c.avatar_url} alt="" className="w-16 h-16 border border-white/10 object-cover" />
          ) : (
            <div className="w-16 h-16 bg-white/5 border border-white/10 flex items-center justify-center display-heading text-2xl font-black">
              {c.display_name?.[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <h1 className="display-heading text-3xl font-black">{c.display_name}</h1>
            <div className="kbd-label text-white/40 mt-1">@{c.slug} · {c.status}</div>
          </div>
          {shareUrl ? (
            <div className="flex gap-2">
              <button onClick={() => copy(shareUrl)} data-testid="creator-detail-copy-link" className="neon-btn px-4 py-2 kbd-label inline-flex items-center gap-2">
                <Copy size={14} weight="bold" /> COPY REFERRAL LINK
              </button>
              <button onClick={mintInvite} disabled={minting} data-testid="creator-mint-invite" className="ghost-btn px-4 py-2 kbd-label inline-flex items-center gap-2 text-white disabled:opacity-50">
                <ArrowsClockwise size={14} className={minting ? "animate-spin" : ""} /> {minting ? "GENERATING…" : "REGENERATE"}
              </button>
            </div>
          ) : (
            <button onClick={mintInvite} disabled={minting} data-testid="creator-mint-invite" className="neon-btn px-4 py-2 kbd-label inline-flex items-center gap-2 disabled:opacity-50">
              <Sparkle size={14} weight="fill" /> {minting ? "GENERATING…" : "GENERATE DISCORD INVITE"}
            </button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex gap-1 border-b border-white/10">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`creator-tab-${t.key}`}
            className={`px-4 py-3 kbd-label inline-flex items-center gap-2 border-b-2 transition-colors ${tab === t.key ? "border-neon-cyan text-neon-cyan" : "border-transparent text-white/50 hover:text-white"}`}>
            <t.icon size={14} weight="fill" /> {t.label}
          </button>
        ))}
      </nav>

      {tab === "overview" && (
        <div className="space-y-6">
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="CLICKS" value={m.clicks} />
            <Stat label="CURRENT MEMBERS" value={m.current_members} accent="#E4FF00" />
            <Stat label="LEFT" value={m.left_members} accent="#FF3B30" />
            <Stat label={`NET REVENUE · ${m.attribution_window_days === 0 ? "∞" : m.attribution_window_days + "D"} WINDOW`} value={`$${m.net_revenue}`} accent="#22C55E" />
          </section>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 card-tactical p-6">
              <h3 className="display-heading font-bold text-lg mb-3">CONVERSION FUNNEL</h3>
              <FunnelBars data={m.funnel} />
            </div>
            <div className="card-tactical p-6">
              <h3 className="display-heading font-bold text-lg mb-3">RATES</h3>
              <div className="space-y-2 font-mono text-sm">
                <Row label="Verify rate" val={`${m.verify_rate}%`} />
                <Row label="Join rate" val={`${m.join_rate}%`} />
                <Row label="Retention rate" val={`${m.retention_rate}%`} />
                <Row label="Conversion rate" val={`${m.conversion_rate}%`} />
                <div className="border-t border-white/10 pt-2 mt-2">
                  <Row label={`Commission (${m.commission_pct}%)`} val={`$${m.creator_earnings}`} good />
                  <Row label="Out-of-window" val={`$${m.out_of_window_gross}`} bad />
                </div>
              </div>
            </div>
          </div>

          {/* Earnings + publish gate */}
          <section className="card-tactical p-6 border-l-4 border-l-emerald-500" data-testid="creator-earnings-panel">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex gap-8 flex-wrap">
                <div>
                  <div className="kbd-label text-white/50">EARNED THIS MONTH</div>
                  <div className="stat-value text-3xl text-emerald-400 mt-1" data-testid="owner-earned-month">${data.earnings?.earned_this_month ?? 0}</div>
                </div>
                <div>
                  <div className="kbd-label text-white/50">EARNED ALL-TIME</div>
                  <div className="stat-value text-3xl text-emerald-400 mt-1" data-testid="owner-earned-total">${data.earnings?.earned_all_time ?? 0}</div>
                </div>
                <div>
                  <div className="kbd-label text-white/50">RATE</div>
                  <div className="stat-value text-3xl mt-1">{data.earnings?.commission_pct ?? c.commission_pct}%</div>
                  <div className="text-[10px] text-white/40 font-mono">after {data.earnings?.tebex_fee_pct ?? 0}% Tebex fee + tax · {data.earnings?.attribution_window_days ?? c.attribution_window_days}d window</div>
                </div>
                {(data.earnings?.out_of_window_revenue ?? 0) !== 0 && (
                  <button onClick={openOow} className="text-left group" data-testid="owner-oow-open">
                    <div className="kbd-label text-white/50 group-hover:text-neon-cyan transition-colors">OUT-OF-WINDOW REVENUE ›</div>
                    <div className="stat-value text-3xl text-white/40 mt-1 group-hover:text-white/70 transition-colors" data-testid="owner-oow-revenue">${data.earnings?.out_of_window_revenue}</div>
                    <div className="text-[10px] text-white/40 font-mono max-w-[190px]">owner-only · click to audit · ${data.earnings?.excluded_out_of_window} commission NOT paid, hidden from creator</div>
                  </button>
                )}
              </div>
              <div className="shrink-0">
                <div className="kbd-label text-white/50 mb-2">CREATOR PORTAL VISIBILITY</div>
                {c.earnings_published ? (
                  <button onClick={() => togglePublish(false)} data-testid="earnings-unpublish-btn"
                    className="ghost-btn px-4 py-2 kbd-label inline-flex items-center gap-2 text-emerald-400 hover:!border-red-500">
                    <Eye size={14} weight="fill" /> PUBLISHED · CLICK TO HIDE
                  </button>
                ) : (
                  <button onClick={() => togglePublish(true)} data-testid="earnings-publish-btn"
                    className="neon-btn px-4 py-2 kbd-label inline-flex items-center gap-2">
                    <EyeSlash size={14} weight="fill" /> HIDDEN · PUBLISH TO CREATOR
                  </button>
                )}
                <div className="text-[10px] text-white/40 font-mono mt-1 max-w-[220px]">Until published, the creator sees "pending" instead of a number.</div>
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === "members" && (
        <div className="card-tactical overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="kbd-label text-left text-white/50 border-b border-white/10">
                <th className="px-6 py-3">FAN</th>
                <th>STAGE</th>
                <th>PLAYTIME</th>
                <th>LAST CONNECTED</th>
                <th>PLAYER</th>
                <th>JOINED DISCORD</th>
                <th>SPEND</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {data.members.map(mm => {
                const stage = STAGE_META[mm.funnel_stage] || STAGE_META.invited;
                return (
                  <tr key={mm.discord_id} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="px-6 py-3">
                      <div className="font-sans">@{mm.username || "unknown"}</div>
                      <div className="text-[10px] text-white/40">{mm.discord_id}</div>
                    </td>
                    <td>
                      <span className={`kbd-label px-2 py-0.5 border ${stage.color}`} data-testid={`fan-stage-${mm.discord_id}`}>
                        {stage.label}
                      </span>
                    </td>
                    <td className="text-xs">
                      {mm.is_playing_now ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          <span>{playtimeDisplay(mm)} · LIVE</span>
                        </span>
                      ) : (
                        <span className="text-white/70">{playtimeDisplay(mm)}</span>
                      )}
                    </td>
                    <td className="text-xs text-white/60">
                      {mm.fivem_last_seen ? mm.fivem_last_seen.slice(0, 16).replace("T", " ") : <span className="text-white/20">never</span>}
                    </td>
                    <td className="text-xs">
                      {mm.fivem_player_name ? (
                        <div>
                          <div className="text-white">{mm.fivem_player_name}</div>
                          {mm.fivem_id && <div className="text-[10px] text-white/40">cfx {mm.fivem_id}</div>}
                        </div>
                      ) : <span className="text-white/30">—</span>}
                    </td>
                    <td className="text-xs text-white/60">{mm.first_joined_at?.slice(0, 10) || "—"}</td>
                    <td className="text-emerald-400">{mm.spend_net > 0 ? `$${mm.spend_net}` : "—"}</td>
                    <td>
                      <button onClick={() => setOpenMember(mm.discord_id)} data-testid={`member-open-${mm.discord_id}`} className="ghost-btn px-3 py-1 text-xs text-white">DETAILS</button>
                    </td>
                  </tr>
                );
              })}
              {data.members.length === 0 && <tr><td colSpan={8} className="px-6 py-6 text-white/40">No fans attributed yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "links" && (
        <div className="card-tactical p-6 space-y-4">
          <div>
            <div className="kbd-label text-neon-cyan">SHAREABLE REFERRAL LINK</div>
            <p className="text-xs text-white/50 font-mono mt-1">This is the creator's Discord invite. Joins via this invite are automatically attributed by the bot — no click wrapper, no subdomain.</p>
          </div>
          {shareUrl ? (
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="flex-1 font-mono text-base bg-black/50 border border-white/10 px-4 py-3 truncate select-all" data-testid="creator-links-shareable">{shareUrl}</div>
              <button onClick={() => copy(shareUrl)} data-testid="creator-links-copy" className="neon-btn px-5 py-3 inline-flex items-center gap-2 whitespace-nowrap"><Copy size={14} weight="bold" /> COPY</button>
              <button onClick={mintInvite} disabled={minting} data-testid="creator-links-regenerate" className="ghost-btn px-4 py-3 text-white inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-50"><ArrowsClockwise size={14} className={minting ? "animate-spin" : ""} /> REGENERATE</button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button onClick={mintInvite} disabled={minting} data-testid="creator-links-generate" className="neon-btn px-5 py-3 inline-flex items-center gap-2 disabled:opacity-50">
                <Sparkle size={16} weight="fill" /> {minting ? "GENERATING…" : "GENERATE UNIQUE DISCORD INVITE"}
              </button>
              <span className="text-xs text-white/50 font-mono">Bot mints a never-expiring, unlimited-use invite and sets it as this creator's link.</span>
            </div>
          )}
        </div>
      )}

      {tab === "sales" && (
        <div className="card-tactical overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="kbd-label text-left text-white/50 border-b border-white/10">
              <th className="px-6 py-3">TX</th><th>KIND</th><th>AMOUNT</th><th>DISCORD</th><th>BATCH</th><th>WHEN</th>
            </tr></thead>
            <tbody className="font-mono">
              {data.purchases.slice(0, 500).map(p => (
                <tr key={p.transaction_id} className="border-b border-white/5">
                  <td className="px-6 py-3 truncate max-w-[220px]">{p.transaction_id}</td>
                  <td className={p.kind === "payment" ? "text-emerald-400" : "text-red-400"}>{p.kind}</td>
                  <td>${p.amount} {p.currency}</td>
                  <td className="text-white/60">{p.discord_id || "—"}</td>
                  <td className="text-neon-yellow">{p.batch_label || "—"}</td>
                  <td className="text-white/50 text-xs">{(p.raw_date || p.created_at)?.slice(0, 10)}</td>
                </tr>
              ))}
              {data.purchases.length === 0 && <tr><td colSpan={6} className="px-6 py-6 text-white/40">No purchases yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "profile" && (
        <div className="space-y-6">
          <section className="card-tactical p-6 border-l-4 border-l-neon-yellow" data-testid="creator-login-credentials">
            <h3 className="display-heading font-bold text-lg mb-1">LOGIN CREDENTIALS</h3>
            <p className="text-xs text-white/50 font-mono mb-4">The creator signs in at <span className="text-neon-cyan">/creator/login</span> with this email + password.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <ProfField label="LOGIN EMAIL" val={profile.email} onChange={(v) => setProfile({...profile, email: v})} type="email" placeholder="creator@example.com" hint="Save via SAVE PROFILE below" />
              <div>
                <div className="kbd-label mb-1">
                  {c.has_password ? "RESET PASSWORD" : "SET PASSWORD"}
                  <span className={`ml-2 ${c.has_password ? "text-emerald-400" : "text-red-400"}`}>
                    {c.has_password ? "· password set" : "· no password yet"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="min 6 characters"
                    data-testid="creator-set-password-input"
                    className="flex-1 bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none"
                  />
                  <button onClick={savePassword} disabled={savingPw} data-testid="creator-set-password-btn" className="neon-btn px-4 py-2 kbd-label whitespace-nowrap disabled:opacity-50">
                    {savingPw ? "…" : "SAVE PW"}
                  </button>
                </div>
                <div className="text-[10px] text-white/40 mt-1 font-mono">Shown as text so you can copy &amp; share it once.</div>
                <button
                  onClick={sendCredentials}
                  disabled={sendingCreds || !c.email}
                  data-testid="creator-send-credentials-btn"
                  className="mt-3 w-full ghost-btn px-4 py-2 kbd-label inline-flex items-center justify-center gap-2 text-neon-cyan hover:!border-neon-cyan disabled:opacity-40"
                  title={c.email ? "Email login link + password to this creator" : "Add a login email first"}
                >
                  <PaperPlaneTilt size={14} weight="fill" />
                  {sendingCreds ? "SENDING…" : newPw ? "EMAIL THIS PASSWORD" : "EMAIL LOGIN + AUTO PASSWORD"}
                </button>
                <div className="text-[10px] text-white/40 mt-1 font-mono">
                  {newPw
                    ? "Will email the password above and set it as their login."
                    : "Generates a fresh 12-char password, sets it, and emails the link."}
                </div>
              </div>
            </div>
          </section>

          <section className="card-tactical p-6" data-testid="owner-payout-details">
            <div className="flex items-center gap-2 mb-3">
              <CurrencyDollar size={20} color="#E4FF00" weight="fill" />
              <h3 className="display-heading font-bold text-lg">PAYOUT DETAILS</h3>
              <span className="kbd-label text-white/40">· SET BY CREATOR</span>
            </div>
            {c.payout_details && c.payout_details.method ? (
              <div className="grid md:grid-cols-4 gap-4 font-mono text-sm">
                <div><div className="kbd-label text-white/40">METHOD</div><div className="mt-1 uppercase text-neon-cyan" data-testid="payout-view-method">{c.payout_details.method}</div></div>
                <div><div className="kbd-label text-white/40">HANDLE</div><div className="mt-1 break-all">{c.payout_details.handle || "—"}</div></div>
                <div><div className="kbd-label text-white/40">PHONE</div><div className="mt-1">{c.payout_details.phone || "—"}</div></div>
                <div><div className="kbd-label text-white/40">NOTE</div><div className="mt-1 break-all">{c.payout_details.note || "—"}</div></div>
              </div>
            ) : (
              <div className="text-sm text-white/50 font-mono">Creator hasn't added payout details yet.</div>
            )}
          </section>

          <section className="card-tactical p-6" data-testid="creator-promo-offer">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Tag size={20} color="#E4FF00" weight="fill" />
                <h3 className="display-heading font-bold text-lg">TEBEX PROMO OFFER</h3>
              </div>
              <span className={`kbd-label px-3 py-1 border ${c.promo_published ? "text-emerald-400 border-emerald-500/40" : "text-white/40 border-white/20"}`} data-testid="promo-status-badge">
                {c.promo_published ? "PUBLISHED" : "HIDDEN"}
              </span>
            </div>
            <p className="text-xs text-white/50 font-mono mb-4">Enter a unique discount code &amp; offer. When published, it shows on this creator's portal as "Use code X to get …".</p>
            <div className="grid md:grid-cols-2 gap-4">
              <ProfField label="PROMO CODE" val={profile.promo_code} onChange={(v) => setProfile({...profile, promo_code: v})} placeholder="NOVA20" hint="What the audience types at checkout" />
              <ProfField label="OFFER TEXT" val={profile.promo_description} onChange={(v) => setProfile({...profile, promo_description: v})} placeholder="20% off your first purchase" hint="What the code gives them" />
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button onClick={saveProfile} data-testid="promo-save-btn" className="neon-btn px-5 py-2.5">SAVE OFFER</button>
              {c.promo_published ? (
                <button onClick={() => togglePromoPublish(false)} data-testid="promo-unpublish-btn" className="ghost-btn px-5 py-2.5 text-white kbd-label">UNPUBLISH</button>
              ) : (
                <button onClick={() => togglePromoPublish(true)} data-testid="promo-publish-btn" className="px-5 py-2.5 kbd-label bg-emerald-600 hover:bg-emerald-500 text-white transition-colors">PUBLISH TO CREATOR</button>
              )}
            </div>
          </section>

          <div className="grid md:grid-cols-2 gap-4">
            <ProfField label="STATUS" val={profile.status} onChange={(v) => setProfile({...profile, status: v})} select={["active", "disabled"]} />
            <ProfField label="AVATAR URL" val={profile.avatar_url} onChange={(v) => setProfile({...profile, avatar_url: v})} />
            <ProfField label="CONTACT EMAIL" val={profile.contact_email} onChange={(v) => setProfile({...profile, contact_email: v})} type="email" />
            <ProfField label="DISCORD INVITE URL" val={profile.discord_invite_url} onChange={(v) => setProfile({...profile, discord_invite_url: v})} placeholder="https://discord.gg/…" />
            <ProfField label="TEBEX REFERRAL URL" val={profile.tebex_referral_url} onChange={(v) => setProfile({...profile, tebex_referral_url: v})} placeholder="https://…tebex.io/…" hint="Creator's Tebex store referral link" />
            <ProfField label="COMMISSION %" val={profile.commission_pct} onChange={(v) => setProfile({...profile, commission_pct: v})} type="number" step="0.1" />
            <ProfField label="ATTRIBUTION WINDOW (DAYS)" val={profile.attribution_window_days} onChange={(v) => setProfile({...profile, attribution_window_days: v})} type="number" hint="0 = unlimited" />
            <ProfField label="PRIMARY PLATFORM" val={profile.primary_platform} onChange={(v) => setProfile({...profile, primary_platform: v})} select={["", "tiktok", "youtube", "twitch", "instagram", "x", "other"]} />
            <ProfField label="TIKTOK" val={profile.handle_tiktok} onChange={(v) => setProfile({...profile, handle_tiktok: v})} />
            <ProfField label="YOUTUBE" val={profile.handle_youtube} onChange={(v) => setProfile({...profile, handle_youtube: v})} />
            <ProfField label="TWITCH" val={profile.handle_twitch} onChange={(v) => setProfile({...profile, handle_twitch: v})} />
            <ProfField label="INSTAGRAM" val={profile.handle_instagram} onChange={(v) => setProfile({...profile, handle_instagram: v})} />
            <ProfField label="X / TWITTER" val={profile.handle_x} onChange={(v) => setProfile({...profile, handle_x: v})} />
            <ProfField label="PAYOUT METHOD" val={profile.payout_method} onChange={(v) => setProfile({...profile, payout_method: v})} select={["", "paypal", "bank", "crypto", "other"]} />
            <ProfField label="PAYOUT ADDRESS" val={profile.payout_address} onChange={(v) => setProfile({...profile, payout_address: v})} />
            <ProfField label="PAYOUT MIN THRESHOLD" val={profile.payout_min_threshold} onChange={(v) => setProfile({...profile, payout_min_threshold: v})} type="number" />
            <ProfField label="CONTRACT SIGNED" val={profile.contract_signed_at?.slice(0, 10)} onChange={(v) => setProfile({...profile, contract_signed_at: v})} type="date" />
          </div>
          <div>
            <div className="kbd-label mb-1">NOTES</div>
            <textarea rows={5} value={profile.notes || ""} onChange={(e) => setProfile({...profile, notes: e.target.value})} className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none resize-none" />
          </div>
          <button onClick={saveProfile} data-testid="creator-profile-save" className="neon-btn px-6 py-3">SAVE PROFILE</button>
        </div>
      )}

      <MemberDrawer discordId={openMember} onClose={() => setOpenMember(null)} onSaved={load} />

      <Sheet open={oowOpen} onOpenChange={setOowOpen}>
        <SheetContent side="right" className="!w-full sm:!w-[640px] !max-w-none bg-obsidian-surface border-l border-white/10 overflow-y-auto text-white">
          <SheetHeader>
            <SheetTitle className="display-heading text-2xl">Out-of-window purchases</SheetTitle>
            <SheetDescription className="kbd-label text-white/40">
              OWNER-ONLY · SALES EXCLUDED FROM {c.display_name}'S EARNINGS
            </SheetDescription>
          </SheetHeader>
          {!oow ? (
            <div className="mt-8 kbd-label text-neon-cyan">// LOADING</div>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="flex gap-6 font-mono text-sm">
                <div><span className="kbd-label text-white/40">EXCLUDED SALES</span><div className="text-2xl stat-value">{oow.count}</div></div>
                <div><span className="kbd-label text-white/40">REVENUE</span><div className="text-2xl stat-value text-white/60">${oow.total_revenue}</div></div>
                <div><span className="kbd-label text-white/40">WINDOW</span><div className="text-2xl stat-value">{oow.attribution_window_days}d</div></div>
              </div>
              <div className="space-y-2">
                {oow.rows.map((r, i) => (
                  <div key={`${r.discord_id || ""}-${r.purchase_date || ""}-${i}`} className="card-tactical p-3 font-mono text-xs" data-testid={`oow-row-${i}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <div className="font-sans text-sm">{r.customer_name || r.tebex_username || "—"}</div>
                        <div className="text-white/40">{r.customer_email || r.discord_id}</div>
                        {r.package_names && <div className="text-white/40 mt-0.5">{r.package_names}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className={r.kind === "payment" ? "text-emerald-400" : "text-red-400"}>${r.net}</div>
                        <span className={`kbd-label ${r.reason === "before_join" ? "text-neon-yellow" : "text-white/40"}`}>
                          {r.reason === "before_join" ? "BEFORE JOIN" : "PAST WINDOW"}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-4 text-white/50 border-t border-white/5 pt-2">
                      <span>Bought: {(r.purchase_date || "").slice(0, 10)}</span>
                      <span>Joined: {(r.joined_at || "—").slice(0, 10)}</span>
                      <span className="text-white/30">would've earned ${r.commission_would_be}</span>
                    </div>
                  </div>
                ))}
                {oow.rows.length === 0 && <div className="text-white/40 text-sm">No out-of-window purchases.</div>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Stat({ label, value, accent = "#00F0FF" }) {
  return (
    <div className="card-tactical p-5">
      <div className="kbd-label">{label}</div>
      <div className="stat-value text-3xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Row({ label, val, good, bad }) {
  return (
    <div className="flex justify-between">
      <span className="text-white/60">{label}</span>
      <span className={good ? "text-emerald-400" : bad ? "text-red-400" : "text-white"}>{val}</span>
    </div>
  );
}

function ProfField({ label, hint, val, onChange, type = "text", step, placeholder, select }) {
  return (
    <div>
      <div className="kbd-label mb-1">{label}</div>
      {select ? (
        <select value={val || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm">
          {select.map(o => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      ) : (
        <input value={val || ""} type={type} step={step} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none" />
      )}
      {hint && <div className="text-[10px] text-white/40 mt-1 font-mono">{hint}</div>}
    </div>
  );
}
